import { useEffect } from 'react';
import { buildCanonicalPageUrl, canonicalizeReferrer } from 'utils/umamiCanonical';

let lastTrackedCanonicalUrl: string | null = null;

export const resetTracking = () => {
  lastTrackedCanonicalUrl = null;
};

export interface UmamiEventProps {
  url?: string;
  hostname?: string;
  referrer?: string;
  website?: string;
}

export interface UmamiInstance {
  track(overrides: (props: UmamiEventProps) => UmamiEventProps): void;
}

declare global {
  interface Window {
    umami?: UmamiInstance;
  }
}

interface UmamiAnalyticsProps {
  umamiWebsiteId: string;
  pageId: string;
  canonicalHostname?: string | null;
}

/**
 * Injects the Umami collect script and fires an explicit pageview on each
 * content page mount.
 *
 * data-auto-track="false" disables Umami's own pageview firing. We call
 * track() ourselves so every event passes through canonicalization.
 *
 * The script is injected imperatively rather than via JSX so the same DOM
 * node persists across SPA navigations — window.umami survives and we track
 * directly on the second page onward without waiting for the network.
 *
 * CMP exemption: Umami is self-hosted and configured with no cookies and no
 * cross-site tracking. It qualifies as a cookieless, privacy-preserving
 * analytics tool and is intentionally loaded without a CMP consent gate —
 * the same basis as EqualWeb. It should be classified as "Analytics - No
 * Consent Required" in the ConsentManager dashboard.
 */
export function UmamiAnalytics({ umamiWebsiteId, pageId, canonicalHostname }: UmamiAnalyticsProps) {
  const canonicalPageUrl = buildCanonicalPageUrl(pageId);

  useEffect(() => {
    const doTrack = () => {
      const referrer =
        lastTrackedCanonicalUrl ??
        canonicalizeReferrer(document.referrer, window.location.hostname, canonicalHostname, umamiWebsiteId);
      window.umami?.track((props) => ({
        ...props,
        url: canonicalPageUrl,
        hostname: umamiWebsiteId,
        website: umamiWebsiteId,
        referrer,
      }));
      lastTrackedCanonicalUrl = canonicalPageUrl;
    };

    if (window.umami) {
      doTrack();
      return;
    }

    let scriptEl = document.querySelector<HTMLScriptElement>(`script[data-website-id="${CSS.escape(umamiWebsiteId)}"]`);
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.src = '/zca/script.js';
      scriptEl.setAttribute('data-website-id', umamiWebsiteId);
      scriptEl.setAttribute('data-auto-track', 'false');
      document.head.appendChild(scriptEl);
    }

    const element = scriptEl;
    const handleError = () => element.remove();
    element.addEventListener('load', doTrack, { once: true });
    element.addEventListener('error', handleError, { once: true });
    return () => {
      element.removeEventListener('load', doTrack);
      element.removeEventListener('error', handleError);
    };
  }, [canonicalPageUrl, umamiWebsiteId, canonicalHostname]);

  return null;
}
